// ./src/utils/api/CarMonthlyRevenueService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { CarMonthlySummary } from "./CarMonthlySummaryService";
import { RevenueKind } from "./AppEssentialsService";

// GraphQL queries
const CAR_MONTHLY_REVENUE_LIST_QUERY = gql`
  query CarMonthlyRevenueList(
    $filter: CarMonthlyRevenueFilter
    $params: PaginationAndSorting
  ) {
    carMonthlyRevenueList(filter: $filter, params: $params) {
      code
      data {
        carMonthlySummaryId
        revenueKindId
        recordsCount
        amount
        carMonthlySummary {
          id
          carId
          homeCurrency
          year
          month
        }
        revenueKind {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_MONTHLY_REVENUE_GET_QUERY = gql`
  query CarMonthlyRevenueGet($carMonthlySummaryId: ID, $revenueKindId: Int) {
    carMonthlyRevenueGet(carMonthlySummaryId: $carMonthlySummaryId, revenueKindId: $revenueKindId) {
      code
      data {
        carMonthlySummaryId
        revenueKindId
        recordsCount
        amount
        carMonthlySummary {
          id
          carId
          homeCurrency
          year
          month
        }
        revenueKind {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface CarMonthlyRevenue {
  carMonthlySummaryId?: string | undefined;
  revenueKindId?: number | undefined;
  recordsCount?: number | undefined;
  amount?: number | undefined;
  // Relations
  carMonthlySummary?: CarMonthlySummary | undefined;
  revenueKind?: RevenueKind | undefined;
}

export interface CarMonthlyRevenueFilter {
  carMonthlySummaryId?: string[] | null | undefined;
  revenueKindId?: number[] | null | undefined;
}

export interface CarMonthlyRevenueWhereInput {
  carMonthlySummaryId?: string | undefined;
  revenueKindId?: number | undefined;
}

/**
 * CarMonthlyRevenue service that handles car monthly revenue-related API calls.
 * This is a read-only service for viewing aggregated monthly revenue statistics
 * per car monthly summary and revenue kind.
 * Supports: list, get (composite key)
 */
export class CarMonthlyRevenueService {
  /**
   * Get a list of car monthly revenues with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarMonthlyRevenueFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_MONTHLY_REVENUE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single car monthly revenue by composite key (carMonthlySummaryId + revenueKindId)
   */
  static async get(
    carMonthlySummaryId: string,
    revenueKindId: number
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_MONTHLY_REVENUE_GET_QUERY, {
      carMonthlySummaryId,
      revenueKindId,
    });
    return result;
  }
}